/**
 * Met le HTML des rubriques narratives sous une forme unique,
 * sans modifier les mots :
 * - un bloc de texte = un <p>…</p>
 * - <br><br> (et plus) sépare deux paragraphes
 * - un seul <br> reste un saut de ligne dans le paragraphe
 * - l'espace entre deux balises inline (NOM / prénom) devient &nbsp;
 *   pour survivre à TinyMCE et à l'export .docx
 * - l'espace sécable avant « : » devient insécable
 *
 * Les rubriques à mise en page propre (fait, personnes, véhicules, parties)
 * ne sont pas restructurées. L'espace avant « : » y devient tout de même insécable.
 */

const SKIP_RUBRIC_HTML_VERSIONS = new Set([
  'faits-v1',
  'pers-concernees-v1',
  'vehicules-impliques-v1',
  'parties-v1',
]);

const INLINE_TAGS = 'strong|b|em|i|u|s|strike|span|sub|sup|a|mark|font';
const BLOCK_TAGS = new Set([
  'p', 'div', 'ul', 'ol', 'table', 'blockquote',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
]);

const protectInlineSpaces = (html) => {
  const re = new RegExp(
    `(</(?:${INLINE_TAGS})>)(?:\\s|&nbsp;|&#160;)+(<(?:${INLINE_TAGS})(?=[\\s>/]))`,
    'gi'
  );
  let current = html;
  let previous;
  do {
    previous = current;
    current = current.replace(re, '$1&nbsp;$2');
  } while (current !== previous);
  return current;
};

const prepareRubricHtml = (html) => {
  let current = html.replace(/\r\n?/g, '\n');
  current = current.replace(/<br\s*\/?>/gi, '<br>');
  // Les retours source ne créent pas de paragraphe à l'écran : ils valent un espace.
  // Les écrire comme espace évite que TinyMCE colle les mots.
  current = current.replace(/[ \t]*\n+[ \t]*/g, ' ');
  return protectInlineSpaces(current);
};

const isBlockTag = (tag) => BLOCK_TAGS.has(String(tag || '').toLowerCase());

const indexAfterElement = (html, start) => {
  const open = html.slice(start).match(/^<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/);
  if (!open) return null;
  const tag = open[1];
  const openRe = new RegExp(`^<${tag}(?![a-zA-Z0-9])[^>]*>`, 'i');
  const closeRe = new RegExp(`^</${tag}\\s*>`, 'i');
  let depth = 1;
  let i = start + open[0].length;
  while (i < html.length && depth > 0) {
    if (html[i] !== '<') {
      i += 1;
      continue;
    }
    const rest = html.slice(i);
    const close = rest.match(closeRe);
    if (close) {
      depth -= 1;
      i += close[0].length;
      continue;
    }
    const nextOpen = rest.match(openRe);
    if (nextOpen) {
      depth += 1;
      i += nextOpen[0].length;
      continue;
    }
    i += 1;
  }
  return depth === 0 ? i : null;
};

const parseTopLevel = (html) => {
  const nodes = [];
  let text = '';
  let i = 0;
  const pushText = () => {
    if (!text) return;
    nodes.push({ kind: 'text', value: text });
    text = '';
  };

  while (i < html.length) {
    if (/^<br>/i.test(html.slice(i, i + 4))) {
      text += '<br>';
      i += 4;
      continue;
    }
    if (html[i] === '<') {
      const open = html.slice(i).match(/^<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/);
      if (open && isBlockTag(open[1])) {
        const end = indexAfterElement(html, i);
        if (end != null && end > i) {
          pushText();
          nodes.push({ kind: 'block', value: html.slice(i, end) });
          i = end;
          continue;
        }
      }
    }
    text += html[i];
    i += 1;
  }
  pushText();
  return nodes;
};

const splitOuter = (html) => {
  const open = html.match(/^<([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/);
  if (!open) return null;
  const tag = open[1].toLowerCase();
  const close = `</${tag}>`;
  if (html.slice(-close.length).toLowerCase() !== close) return null;
  return {
    tag,
    attrs: open[2] || '',
    inner: html.slice(open[0].length, html.length - close.length),
  };
};

const isVisuallyEmpty = (html) => {
  if (/<(img|table|ul|ol|hr|svg)\b/i.test(html)) return false;
  const text = html
    .replace(/<br>/gi, '')
    .replace(/&nbsp;|&#160;/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s/g, '');
  return text.length === 0;
};

const cleanupParagraph = (chunk) => {
  const cleaned = chunk
    .replace(/^(?:\s|<br>)+/gi, '')
    .replace(/(?:\s|<br>)+$/gi, '');
  if (!cleaned || isVisuallyEmpty(cleaned)) return '';
  return cleaned;
};

const renderBlock = (html) => {
  const parts = splitOuter(html);
  if (!parts) return [html];
  const { tag, attrs, inner } = parts;

  if (tag === 'p' || tag === 'div') {
    return paragraphsFromFragment(inner, attrs);
  }
  if (tag === 'ul' || tag === 'ol') {
    return [html.replace(/(?:<br>\s*){2,}/gi, '<br>')];
  }
  if (tag === 'blockquote') {
    const innerHtml = paragraphsFromFragment(inner, '').join('');
    return [`<blockquote${attrs}>${innerHtml}</blockquote>`];
  }
  return [html];
};

const splitParagraphBreaks = (html) => {
  const parts = [];
  let buf = '';
  let i = 0;
  let depth = 0;

  while (i < html.length) {
    if (html[i] !== '<') {
      buf += html[i];
      i += 1;
      continue;
    }

    const br = html.slice(i).match(/^(?:<br>\s*){2,}/i);
    if (br && depth === 0) {
      parts.push(buf);
      buf = '';
      i += br[0].length;
      continue;
    }

    const end = html.indexOf('>', i);
    if (end === -1) {
      buf += html.slice(i);
      break;
    }
    const tag = html.slice(i, end + 1);
    buf += tag;
    if (/^<(?:br|img|hr|input|meta|link|source|col|wbr)\b/i.test(tag) || /\/>$/.test(tag) || /^<!--/.test(tag)) {
      i = end + 1;
      continue;
    }
    if (/^<\//.test(tag)) depth = Math.max(0, depth - 1);
    else if (/^<[a-zA-Z]/.test(tag)) depth += 1;
    i = end + 1;
  }

  parts.push(buf);
  return parts;
};

const paragraphsFromFragment = (html, attrs) => {
  const out = [];
  parseTopLevel(html).forEach((node) => {
    if (node.kind === 'block') {
      out.push(...renderBlock(node.value));
      return;
    }
    if (!node.value.replace(/\s/g, '')) return;
    splitParagraphBreaks(node.value).forEach((chunk) => {
      const cleaned = cleanupParagraph(chunk);
      if (!cleaned) return;
      out.push(`<p${attrs}>${cleaned}</p>`);
    });
  });
  return out;
};

const rebuildRubricHtml = (html) => {
  const out = [];
  parseTopLevel(html).forEach((node) => {
    if (node.kind === 'block') {
      out.push(...renderBlock(node.value));
      return;
    }
    if (!node.value.replace(/\s/g, '')) return;
    out.push(...paragraphsFromFragment(node.value, ''));
  });
  return out.join('');
};

// Une espace sécable collée à « : » (y compris après une balise : </u> : )
// devient &nbsp;, pour que le deux-points ne passe pas à la ligne.
// Une espace déjà insécable n'est pas réécrite.
const BREAKING_SPACE_BEFORE_COLON =
  /(?:[ \t\u00A0]|&nbsp;|&#160;|&#x0*a0;)*[ \t](?:[ \t\u00A0]|&nbsp;|&#160;|&#x0*a0;)*(?=:)/gi;

const protectColonSpaces = (html) => html.replace(/(^|>)([^<]+)/g, (match, start, text) => (
  start + text.replace(BREAKING_SPACE_BEFORE_COLON, '&nbsp;')
));

const normalizeRubricHtml = (html, options = {}) => {
  if (!html || typeof html !== 'string') return html || '';
  if (options.version && SKIP_RUBRIC_HTML_VERSIONS.has(options.version)) {
    return protectColonSpaces(html);
  }

  const prepared = prepareRubricHtml(html);
  const structured = options.mode === 'inline' ? prepared : rebuildRubricHtml(prepared);
  return protectColonSpaces(structured);
};

const normalizeEditorHtmlSpaces = (html) => normalizeRubricHtml(html, { mode: 'inline' });

export {
  normalizeRubricHtml,
  normalizeEditorHtmlSpaces,
  SKIP_RUBRIC_HTML_VERSIONS,
};
